import firebase from '../../config';

// Helper function to get PDF download URL from path (same as video recordings)
export const getPdfDownloadUrl = async (pdfPath) => {
  if (!pdfPath) return null;
  
  try {
    const storageRef = firebase.storage().ref(`submissions/${pdfPath}`);
    return await storageRef.getDownloadURL();
  } catch (error) {
    console.error('Failed to get PDF download URL:', error);
    return null;
  }
};

// Submission timestamps as day/month/year, e.g. "07/08/2026, 03:03:20 PM".
// Formatted explicitly rather than via toLocaleString so the exported sheet reads the same
// whatever locale the machine doing the export happens to use.
export const formatSubmittedAt = (submittedAt) => {
    if (!submittedAt) return 'N/A';
    // Anything already stored as text is passed through - only timestamps can be reformatted
    if (typeof submittedAt !== 'number') return submittedAt;

    const date = new Date(submittedAt);
    if (isNaN(date.getTime())) return 'N/A';

    const pad = (value) => String(value).padStart(2, '0');
    const hours = date.getHours();
    const hour12 = hours % 12 || 12;
    const meridiem = hours < 12 ? 'AM' : 'PM';

    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}, ` +
        `${pad(hour12)}:${pad(date.getMinutes())}:${pad(date.getSeconds())} ${meridiem}`;
};

// Utility function to strip HTML tags for export
export const stripHtmlTags = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') return htmlString || '';
    
    // Create a temporary div element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    // Get text content and clean up whitespace
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up extra whitespace and newlines
    return textContent.replace(/\s+/g, ' ').trim();
};

export const getMaxScore = (exam) => {
    if (!exam || !exam.sections) return 0;
    return exam.sections.reduce((total, section) => {
      const sectionMaxScore = section.questions.reduce((secTotal, q) => {
        return secTotal + (q.maxScore || 1);
      }, 0);
      return total + sectionMaxScore;
    }, 0);
};

export const getCorrectOptionIds = (question) => {
    return question.options?.filter(o => o.isCorrect).map(o => o.id) || [];
};

export const getCorrectOptionTexts = (question) => {
    return question.options?.filter(o => o.isCorrect).map(o => o.text) || [];
};

export const calculateSectionScore = (section, submission) => {
    if (!section || !section.questions || !submission) {
        return { rawScore: 0, maxScore: 0, weightage: section?.weightage || 0, weightedScore: 0, questionDetails: [] };
    }

    let rawScore = 0;
    let maxScore = 0;
    const questionDetails = [];

    section.questions.forEach(q => {
        // Get evaluation from aiEvaluation.evaluations
        const evaluation = submission.aiEvaluation?.evaluations?.[q.id];
        if (evaluation) {
            // Use AI evaluation scores
            const questionScore = evaluation.score || 0;
            const questionMaxScore = evaluation.maxScore || 0;
            
            rawScore += questionScore;
            maxScore += questionMaxScore;
            
            // Build question details from AI evaluation
            let userAnswer = '';
            let correctAnswer = '';
            let isCorrect = false;
            
            if (evaluation.evaluationType === 'mcq') {
                userAnswer = Array.isArray(evaluation.selectedOptions) ? evaluation.selectedOptions.join(', ') : (evaluation.selectedOptions || 'Not answered');
                correctAnswer = Array.isArray(evaluation.correctOptions) ? evaluation.correctOptions.join(', ') : (typeof evaluation.correctOptions === 'string' ? evaluation.correctOptions : 'N/A');
                isCorrect = evaluation.score === evaluation.maxScore;
            } else if (evaluation.evaluationType === 'text') {
                // For text questions, check if answer exists in AI evaluation first
                if (evaluation.answer !== undefined && evaluation.answer !== null && evaluation.answer !== '') {
                  if (Array.isArray(evaluation.answer)) {
                    userAnswer = evaluation.answer.join(', ');
                  } else if (typeof evaluation.answer === 'string') {
                    userAnswer = evaluation.answer;
                  } else {
                    userAnswer = 'Not answered';
                  }
                } else {
                  // Fallback to submission.answers if answer not in AI evaluation
                  const ans = submission.answers?.[q.id];
                  if (ans && typeof ans === 'object' && ans.text !== undefined) {
                    // Rich text answer with images/PDF
                    let textContent = '';
                    if (typeof ans.text === 'string') {
                      textContent = ans.text;
                    } else if (ans.text && typeof ans.text === 'object' && ans.text.type === 'storage_reference') {
                      textContent = `[Large text stored in Firebase Storage - ${ans.text.size} characters]`;
                    }
                    
                    userAnswer = textContent || 'No text provided';
                    
                    if (ans.images && ans.images.length > 0) {
                      userAnswer += `\n\nImages: ${ans.images.length} image(s) provided`;
                    }
                    
                    if (ans.pdfFile && (ans.pdfFile.pdfPath || ans.pdfFile.fileName)) {
                      // Show original filename for better user experience
                      const displayName = ans.pdfFile.originalFileName || ans.pdfFile.fileName;
                      const pdfInfo = ans.pdfFile.pdfPath ? 
                        `PDF: ${displayName} (Path: ${ans.pdfFile.pdfPath})` : 
                        `PDF: ${displayName}`;
                      userAnswer += `\n\n${pdfInfo}`;
                    }
                  } else if (typeof ans === 'string') {
                    userAnswer = ans;
                  } else {
                    userAnswer = 'Not answered';
                  }
                }
                correctAnswer = '';
                isCorrect = evaluation.score === evaluation.maxScore;
            } else if (evaluation.evaluationType === 'scenario-text') {
                // For scenario-text questions, check if answer exists in AI evaluation first
                if (evaluation.answer !== undefined && evaluation.answer !== null && evaluation.answer !== '') {
                  userAnswer = evaluation.answer;
                } else {
                  // Fallback to submission.answers if answer not in AI evaluation
                  const ans = submission.answers?.[q.id];
                  if (ans && typeof ans === 'object') {
                    userAnswer = `Selected Scenario: ${ans.selectedScenario || 'Not selected'}\n\nResponse: ${ans.text || 'No response'}`;
                    if (ans.images && ans.images.length > 0) {
                      userAnswer += `\n\nImages: ${ans.images.length} image(s) provided`;
                    }
                  } else {
                    userAnswer = 'Not answered';
                  }
                }
                correctAnswer = evaluation.evaluationPoints ? evaluation.evaluationPoints.join(', ') : '';
                isCorrect = evaluation.score === evaluation.maxScore;
            } else if (evaluation.evaluationType === 'code') {
                // For code questions, check if answer exists in AI evaluation first
                if (evaluation.answer !== undefined && evaluation.answer !== null && evaluation.answer !== '') {
                  userAnswer = evaluation.answer;
                } else {
                  // Fallback to submission.answers if answer not in AI evaluation
                  const ans = submission.answers?.[q.id];
                  if (ans && typeof ans === 'object') {
                    userAnswer = `Language: ${ans.language || 'N/A'}\n${ans.code || ''}`;
                  } else {
                    userAnswer = ans || 'Not answered';
                  }
                }
                correctAnswer = `Code Quality Exam`;
                isCorrect = evaluation.score === evaluation.maxScore;
            } else if (evaluation.evaluationType === 'psyco') {
                // For psyco questions, check if answer exists in AI evaluation first
                if (evaluation.answer !== undefined && evaluation.answer !== null && evaluation.answer !== '') {
                  userAnswer = evaluation.answer;
                } else {
                  // Fallback to submission.answers if answer not in AI evaluation
                  userAnswer = submission.answers?.[q.id] || 'Not answered';
                }
                correctAnswer = evaluation.correctPattern || '';
                isCorrect = evaluation.score === evaluation.maxScore;
            } else if (evaluation.evaluationType === 'video') {
                // For video questions, check if answer exists in AI evaluation first
                if (evaluation.answer !== undefined && evaluation.answer !== null && evaluation.answer !== '') {
                  userAnswer = evaluation.answer;
                } else {
                  // Fallback to submission.answers if answer not in AI evaluation
                  const ans = submission.answers?.[q.id];
                  if (ans && typeof ans === 'object') {
                    const videoRef = ans.videoUrl || ans.videoPath || 'N/A';
                    userAnswer = `Video: ${videoRef}\nSelected Scenario: ${ans.selectedScenario || 'N/A'}`;
                  } else {
                    userAnswer = 'Not answered';
                  }
                }
                correctAnswer = `Video Analysis Scores`;
                isCorrect = evaluation.score === evaluation.maxScore;
                

            }
            
            questionDetails.push({
                questionId: q.id, // Add questionId for re-evaluation
                questionText: q.text,
                userAnswer,
                originalUserAnswer: submission.answers?.[q.id], // Preserve original answer object for video actions
                correctAnswer,
                maxScore: questionMaxScore,
                score: questionScore,
                type: q.type,
                isCorrect,
                feedback: evaluation.feedback,
                confidence: evaluation.confidence,
                textScore: evaluation.textScore, // Add textScore for re-evaluation check
                evaluationType: evaluation.evaluationType,
                criteriaBreakdown: evaluation.criteriaBreakdown,
                selectedScenario: evaluation.selectedScenario,
                evaluationPoints: evaluation.evaluationPoints,
                // Video analysis specific fields
                transcript: evaluation.transcript,
                processingTime: evaluation.processingTime,
                // Raw scores (out of 10)
                rawFinalScore: evaluation.rawFinalScore,
                rawFluencyScore: evaluation.rawFluencyScore,
                rawGrammarScore: evaluation.rawGrammarScore,
                rawPronunciationScore: evaluation.rawPronunciationScore,
                rawMtiScore: evaluation.rawMtiScore,
                // Scaled scores (out of maxScore)
                fluencyScore: evaluation.fluencyScore,
                grammarScore: evaluation.grammarScore,
                pronunciationScore: evaluation.pronunciationScore,
                mtiScore: evaluation.mtiScore
            });
        } else {
            // Fallback for questions without AI evaluation
            const questionMaxScore = q.maxScore !== undefined ? Number(q.maxScore) : 1;
            maxScore += questionMaxScore;
            
            const answer = submission.answers?.[q.id];
            let score = 0;
            let userAnswer = '';
            let correctAnswer = '';
            let isCorrect = false;
            
            if (q.type === 'mcq-single' || q.type === 'mcq-multi') {
                const correctOpts = getCorrectOptionTexts(q);
                correctAnswer = correctOpts.join(', ');
                
                if (q.type === 'mcq-single' && typeof answer === 'string') {
                    const selectedOption = q.options?.find(o => o.id === answer);
                    userAnswer = selectedOption ? selectedOption.text : 'Not answered';
                    score = selectedOption?.isCorrect ? questionMaxScore : 0;
                    isCorrect = selectedOption?.isCorrect || false;
                } else if (q.type === 'mcq-multi' && Array.isArray(answer)) {
                    const selectedOptions = answer.map(optId => q.options?.find(o => o.id === optId)).filter(Boolean);
                    userAnswer = selectedOptions.map(o => o.text).join(', ');
                    const correctSelected = selectedOptions.filter(o => o.isCorrect).length;
                    const totalCorrect = q.options?.filter(o => o.isCorrect).length || 0;
                    score = totalCorrect > 0 ? (correctSelected / totalCorrect) * questionMaxScore : 0;
                    isCorrect = correctSelected === totalCorrect && selectedOptions.length === totalCorrect;
                }
            } else if (q.type === 'scenario-text') {
                // Handle scenario-text questions in fallback
                const ans = submission.answers?.[q.id];
                if (ans && typeof ans === 'object') {
                  userAnswer = `Selected Scenario: ${ans.selectedScenario || 'Not selected'}\n\nResponse: ${ans.text || 'No response'}`;
                  if (ans.images && ans.images.length > 0) {
                    userAnswer += `\n\nImages: ${ans.images.length} image(s) provided`;
                  }
                  if (ans.pdfFile && (ans.pdfFile.pdfPath || ans.pdfFile.fileName)) {
                    // Show original filename for better user experience
                    const displayName = ans.pdfFile.originalFileName || ans.pdfFile.fileName;
                    const pdfInfo = ans.pdfFile.pdfPath ? 
                      `PDF: ${displayName} (Path: ${ans.pdfFile.pdfPath})` : 
                      `PDF: ${displayName}`;
                    userAnswer += `\n\n${pdfInfo}`;
                  }
                } else {
                  userAnswer = answer || 'Not answered';
                }
                correctAnswer = q.scenarioOptions ? 'Scenario-specific criteria' : 'N/A';
                score = 0; // No AI evaluation available
                isCorrect = false;
            } else {
                userAnswer = answer || 'Not answered';
                // Combine evaluationKeywords and evaluationPoints for backward compatibility
                const evalPoints = q.evaluationPoints || '';
                const evalKeywords = q.evaluationKeywords || '';
                if (evalPoints && evalKeywords) {
                    const pointsArray = typeof evalPoints === 'string' 
                        ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                        : Array.isArray(evalPoints) ? evalPoints : [];
                    const keywordsArray = typeof evalKeywords === 'string'
                        ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                        : Array.isArray(evalKeywords) ? evalKeywords : [];
                    const combined = [...pointsArray, ...keywordsArray];
                    correctAnswer = [...new Set(combined)].join(', ');
                } else {
                    correctAnswer = evalPoints || evalKeywords || 'N/A';
                }
                score = 0; // No AI evaluation available
                isCorrect = false;
            }
            
            rawScore += score;
            questionDetails.push({
                questionText: q.text,
                userAnswer,
                correctAnswer,
                maxScore: questionMaxScore,
                score,
                type: q.type,
                isCorrect,
                feedback: 'No AI evaluation available',
                confidence: 0,
                evaluationType: 'fallback'
            });
        }
    });

    const weightage = section.weightage || 0;
    const weightedScore = (maxScore > 0 ? (rawScore / maxScore) : 0) * weightage;
    return {
        rawScore,
        maxScore,
        weightage,
        weightedScore,
        questionDetails
    };
};

export const getQuestionAnalysis = (question, sectionId, submission) => {
    const analysis = {
        isCorrect: false,
        isPartiallyCorrect: false,
        isIncorrect: false,
        score: 0,
        maxScore: question.maxScore || 1,
        selectedAnswer: null,
        correctAnswer: null,
        analysisText: ''
    };

    // Use AI evaluation if available
    const evaluation = submission.aiEvaluation?.evaluations?.[question.id];
    
    if (evaluation) {
        analysis.score = evaluation.score || 0;
        analysis.maxScore = evaluation.maxScore || 1;
        
        if (evaluation.evaluationType === 'mcq') {
            analysis.selectedAnswer = evaluation.selectedOptions?.join(', ') || 'Not answered';
            analysis.correctAnswer = evaluation.correctOptions?.join(', ') || 'N/A';
        } else if (evaluation.evaluationType === 'scenario-text') {
            const ans = submission.answers?.[question.id];
            if (ans && typeof ans === 'object') {
              analysis.selectedAnswer = `Selected Scenario: ${ans.selectedScenario || 'Not selected'}\n\nResponse: ${ans.text || 'No response'}`;
              if (ans.images && ans.images.length > 0) {
                analysis.selectedAnswer += `\n\nImages: ${ans.images.length} image(s) provided`;
              }
            } else {
              analysis.selectedAnswer = 'Not answered';
            }
            analysis.correctAnswer = evaluation.evaluationPoints ? evaluation.evaluationPoints.join(', ') : 'N/A';
        } else {
            analysis.selectedAnswer = submission.answers?.[question.id] || 'Not answered';
            // Combine evaluationKeywords and evaluationPoints for backward compatibility
            const evalPoints = question.evaluationPoints || '';
            const evalKeywords = question.evaluationKeywords || '';
            if (evalPoints && evalKeywords) {
                const pointsArray = typeof evalPoints === 'string' 
                    ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                    : Array.isArray(evalPoints) ? evalPoints : [];
                const keywordsArray = typeof evalKeywords === 'string'
                    ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                    : Array.isArray(evalKeywords) ? evalKeywords : [];
                const combined = [...pointsArray, ...keywordsArray];
                analysis.correctAnswer = [...new Set(combined)].join(', ');
            } else {
                analysis.correctAnswer = evalPoints || evalKeywords || 'N/A';
            }
        }
        
        analysis.analysisText = evaluation.feedback || 'AI evaluated';
        
        if (analysis.score === analysis.maxScore) {
            analysis.isCorrect = true;
        } else if (analysis.score > 0) {
            analysis.isPartiallyCorrect = true;
        } else {
            analysis.isIncorrect = true;
        }
    } else {
        // Fallback logic for questions without AI evaluation
        const answer = submission.answers?.[question.id];
        if (!answer) {
            analysis.analysisText = "Not Attempted";
            return analysis;
        }

        if (question.type === 'mcq-single' || question.type === 'mcq-multi') {
            const correctOptionTexts = getCorrectOptionTexts(question);
            analysis.correctAnswer = correctOptionTexts.join(', ');

            if (question.type === 'mcq-single') {
                const selectedOption = question.options?.find(o => o.id === answer);
                analysis.selectedAnswer = selectedOption?.text || "N/A";
                analysis.score = selectedOption?.isCorrect ? analysis.maxScore : 0;
            } else if (question.type === 'mcq-multi' && Array.isArray(answer)) {
                const selectedOptions = answer.map(optId => question.options?.find(o => o.id === optId)).filter(Boolean);
                analysis.selectedAnswer = selectedOptions.map(o => o.text).join(', ');
                const correctSelected = selectedOptions.filter(o => o.isCorrect).length;
                const totalCorrect = question.options?.filter(o => o.isCorrect).length || 0;
                analysis.score = totalCorrect > 0 ? (correctSelected / totalCorrect) * analysis.maxScore : 0;
            }

            if (analysis.score === analysis.maxScore) {
                analysis.isCorrect = true;
                analysis.analysisText = "Correct";
            } else if (analysis.score > 0) {
                analysis.isPartiallyCorrect = true;
                analysis.analysisText = "Partially Correct";
            } else {
                analysis.isIncorrect = true;
                analysis.analysisText = "Incorrect";
            }
        } else if (question.type === 'scenario-text') {
            const ans = submission.answers?.[question.id];
            if (ans && typeof ans === 'object') {
              analysis.selectedAnswer = `Selected Scenario: ${ans.selectedScenario || 'Not selected'}\n\nResponse: ${ans.text || 'No response'}`;
              if (ans.images && ans.images.length > 0) {
                analysis.selectedAnswer += `\n\nImages: ${ans.images.length} image(s) provided`;
              }
            } else {
              analysis.selectedAnswer = answer || "N/A";
            }
            analysis.score = 0;
            analysis.analysisText = "No AI evaluation available";
        } else {
            analysis.selectedAnswer = answer || "N/A";
            analysis.score = 0;
            analysis.analysisText = "No AI evaluation available";
        }
    }
    
    return analysis;
};

export const calculateTotalScore = (examData, submission, calculateSectionScore) => {
    if (!examData || !examData.sections || !submission) {
        return { totalScore: 0, maxScore: 0, percentage: 0, rawPercentage: 0, sectionScores: [] };
    }

    let totalScore = 0;
    let totalMaxScore = 0;
    let totalWeightedScore = 0;
    const sectionScores = [];

    examData.sections.forEach(section => {
        const sectionResult = calculateSectionScore(section, submission);
        totalScore += sectionResult.rawScore;
        totalMaxScore += sectionResult.maxScore;
        totalWeightedScore += sectionResult.weightedScore;
        sectionScores.push(sectionResult);
    });

    // Calculate raw percentage (totalScore / totalMaxScore * 100)
    const rawPercentage = totalMaxScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;

    return {
        totalScore,
        maxScore: totalMaxScore,
        percentage: totalWeightedScore, // Sum of all section weighted scores
        rawPercentage, // Raw percentage based on total scores
        sectionScores
    };
}; 